import { List, Map } from 'immutable';
import moment from 'moment';
import * as activityConstants from '../../Activity/activity.constants';
import * as dateTimeUtils from '../../../common/utils/dateTimeUtils';
import * as constants from '../emails.constants';
import * as emailConstants from '../../Email/email.constants';
import * as activitySelectorsUtils from '../../Activity/activity.selectors.utils';

/**
 * orders campaigns list and their emails
 * @param campaigns list
 * @param order const value descent or ascend
 */
const getOrderedCampaigns = (campaigns, order) => {
  const orderFactor = order === constants.ORDER_ASCENDING ? 1 : -1;
  const orderedCampaigns = dateTimeUtils.sortArrayByDate(campaigns, [constants.CAMPAIGN_CREATED_ON], orderFactor);
  return orderedCampaigns.map((campaign) => campaign.set(constants.EMAILS,
    dateTimeUtils.sortArrayByDate(campaign.get(constants.EMAILS), ['created', 'date'], orderFactor)));
};

export function getFilteredAndSortedEmailsCampaignsImpl(campaigns, filters, order) {
  let newCampaigns;
  try {
    if (campaigns) {
      newCampaigns = getFilteredCampaigns(campaigns, filters);
      // order campaigns by dates
      newCampaigns = getOrderedCampaigns(newCampaigns, order);
    }
  } catch (err) {
    console.error(err);
  }
  return newCampaigns;
}

export function isCampaignsFiltered(campaigns, filters) {
  let filteredCampaigns;
  try {
    if (campaigns) {
      filteredCampaigns = getFilteredCampaigns(campaigns, filters);
      if (campaigns.size !== filteredCampaigns.size) {
        return true;
      }
      return campaigns.some((campaign, index) =>
        (campaign.get(constants.EMAILS).size !== filteredCampaigns.getIn([index, constants.EMAILS]).size));
    }
  } catch (err) {
    console.error(err);
  }
  return false;
}

export function getContextMenuItemsVisibilityMap(viewStatus) {
  const isActive = viewStatus === activityConstants.ACTIVITY_STATUS_ACTIVE
    || viewStatus === activityConstants.ACTIVITY_STATUS_FUTURE;
  const isPaused = viewStatus === activityConstants.ACTIVITY_STATUS_PAUSED;
  return {
    [activityConstants.CONTEXT_MENU_OPTION_DETAILS]: true,
    [activityConstants.CONTEXT_MENU_OPTION_ACTIVATE]: isPaused,
    [activityConstants.CONTEXT_MENU_OPTION_PAUSE]: isActive,
    [activityConstants.CONTEXT_MENU_OPTION_DEACTIVATE]: isPaused || isActive,
  };
}

function getFilteredCampaigns(campaigns, filters) {
  return campaigns.map((campaign) => {
    const emails = campaign.get(constants.EMAILS);
    const emailsToFilter = filters.get(constants.EMAILS)
      .merge(filters.get(constants.FILTERS_GROUP_NAME_CAMPAIGN))
      .merge(filters.get(activityConstants.FILTER_BY_ACTIVITY));
    const filteredEmails = getFilteredEmailsForCampaigns(emails, emailsToFilter);
    return campaign.set(constants.EMAILS, filteredEmails);
  })
    .filter((campaign) => {
      const status = campaign.get(constants.CAMPAIGN_STATUS);
      const campaignFilters = filters.get(constants.FILTERS_GROUP_NAME_CAMPAIGN);

      const showArchive = status === constants.CAMPAIGN_STATUS_ARCHIVE && campaignFilters.get(constants.FILTERS_CAMPAIGN_ARCHIVED);

      return showArchive || status === constants.CAMPAIGN_STATUS_ACTIVE;
    });
}

const getFilteredEmailsForCampaigns = (emails, filters) => {
  if (!emails) {
    return null;
  }

  return emails
    .filter((email) => {
      if (!email) {
        return false;
      }

      const viewStatus = activitySelectorsUtils.calculateActivityViewStatus(email);
      const searchBY = filters.get(activityConstants.FILTER_SEARCH_BY_ACTIVITY_TEXT);

      if (viewStatus === emailConstants.EMAIL_STATUS_ACTIVE && !filters.get(emailConstants.EMAIL_STATUS_ACTIVE)) {
        return false;
      }

      if (viewStatus === emailConstants.EMAIL_STATUS_INACTIVE && !filters.get(emailConstants.EMAIL_STATUS_INACTIVE)) {
        return false;
      }

      if (viewStatus === emailConstants.EMAIL_STATUS_STOPPED && !filters.get(emailConstants.EMAIL_STATUS_STOPPED)) {
        return false;
      }

      if (searchBY) {
        const activeName = email.get(activityConstants.NAME);
        const activeDescription = email.get(activityConstants.DESCRIPTION);

        if (!((activeName && activeName.toLowerCase().includes(searchBY.toLowerCase())) ||
          (activeDescription && activeDescription.toLowerCase().includes(searchBY.toLowerCase())))) {
          return false;
        }
      }

      return true;
    });
};

export function getEmailOneTimeStatsImpl(stats) {
  return stats ? stats.filter((statsElem) => statsElem.get(constants.EMAIL_STATS_TYPE) === constants.EMAIL_STATS_TYPE_ONE_TIME && statsElem.get(constants.EMAIL_STATS_ACTIVITY_NAME)) : null;
}

export function getEmailRuleStatsImpl(stats) {
  if (stats) {
    const ruleStats = stats.filter((statsElem) => statsElem.get(constants.EMAIL_STATS_TYPE) === constants.EMAIL_STATS_TYPE_RULE && statsElem.get(constants.EMAIL_STATS_ACTIVITY_NAME));
    if (ruleStats) {
      return groupByActivityId(ruleStats);
    }
  }
  return null;
}

export function getEmailMicroCampaignStatsImpl(stats) {
  if (stats) {
    const ruleStats = stats.filter((statsElem) => statsElem.get(constants.EMAIL_STATS_TYPE) === constants.EMAIL_STATS_TYPE_MICROCAMPAIGN && statsElem.get(constants.EMAIL_STATS_ACTIVITY_NAME));
    if (ruleStats) {
      return groupByActivityId(ruleStats);
    }
  }
  return null;
}

export function groupByActivityId(ruleStats) {
  const activityIds = [];
  let stats = new List();
  ruleStats.forEach((item) => {
    if (!activityIds.includes(item.get(constants.EMAIL_STATS_ACTIVITY_ID))) {
      activityIds.push(item.get(constants.EMAIL_STATS_ACTIVITY_ID));
      let stat = Map();
      let campaigns = new List();
      campaigns = campaigns.push(item.get(constants.EMAIL_CAMPAIGN_ID));
      stat = stat
        .set(constants.EMAIL_STATS_ACTIVITY_ID, item.get(constants.EMAIL_STATS_ACTIVITY_ID))
        .set(constants.EMAIL_STATS_ACTIVITY_NAME, item.get(constants.EMAIL_STATS_ACTIVITY_NAME))
        .set(constants.EMAIL_STATS_CAMPAIGNS, campaigns);
      stats = stats.push(stat);
    } else {
      const statsIndex = stats.findIndex((statsElem) => statsElem.get(constants.EMAIL_STATS_ACTIVITY_ID) === item.get(constants.EMAIL_STATS_ACTIVITY_ID));
      let stat = stats.find((statsElem) => statsElem.get(constants.EMAIL_STATS_ACTIVITY_ID) === item.get(constants.EMAIL_STATS_ACTIVITY_ID));
      stat = stat.update(constants.EMAIL_STATS_CAMPAIGNS, (arr) => arr.push(item.get(constants.EMAIL_CAMPAIGN_ID)));
      stats = stats.set(statsIndex, stat);
    }
  });
  return stats;
}

export function getLastSentImpl(stats) {
  if (stats && stats.size > 0) {
    const dates = [];
    stats.forEach((item) => {
      dates.push(item.get(constants.EMAIL_STATS_DATE));
    });
    const maxDate = dateTimeUtils.getMaxDate(dates);
    return stats.find((elem) => moment.utc(elem.get(constants.EMAIL_STATS_DATE)).isSame(maxDate));
  }
  return null;
}
